import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import {
  BadgeDollarSign, ArrowRight, ExternalLink, Loader2,
  CheckCircle2, AlertTriangle, ShieldAlert, Clock,
} from "lucide-react";

/**
 * StripeConnectCard — dashboard payout onboarding + status.
 *
 * Multi-business aware: pass `business` (the currently-selected TBC business)
 * and every API call scopes to that business. Falls back to first-owned
 * when no prop is supplied, matching the legacy single-business callers.
 *
 * States (from backend `derive_stripe_state`):
 *   not_connected · incomplete · verification_required · under_review
 *   · restricted · active
 */
export default function StripeConnectCard({ business }) {
  const businessId = business?.business_id;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const params = businessId ? { business_id: businessId } : {};
      const r = await axios.get(`${API}/connect/status`, {
        params, withCredentials: true,
      });
      setStatus(r.data);
    } catch (e) {
      if (e.response?.status !== 404) {
        // Generic message — never leak Stripe/HTTP raw error to the seller.
        toast.error("We couldn't load payout status. Please try again.");
      }
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const body = { origin_url: window.location.origin };
      if (businessId) body.business_id = businessId;
      const r = await axios.post(
        `${API}/connect/onboarding-link`,
        body,
        { withCredentials: true, timeout: 30000 },
      );
      // Full redirect — Stripe hosts the KYC form.
      window.location.href = r.data.onboarding_url;
    } catch (e) {
      // Generic user-facing copy per spec. Full error is server-logged.
      toast.error("We couldn't open Stripe setup. Please try again.");
      setBusy(false);
    }
  };

  const openStripeDashboard = async () => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/connect/dashboard-link`,
        businessId ? { business_id: businessId } : {},
        { withCredentials: true },
      );
      window.open(r.data.dashboard_url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("We couldn't open your Stripe dashboard. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-gray-100 bg-white p-6" data-testid="connect-card-loading">
        <div className="flex items-center gap-2 text-gray-500 text-sm font-body">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading payout status…
        </div>
      </div>
    );
  }
  if (!status) return null;

  const state = status.state || (status.has_account ? "incomplete" : "not_connected");

  // -------- Not connected --------
  if (state === "not_connected") {
    return (
      <div
        className="rounded-2xl border border-tbc-gold/40 bg-gradient-to-br from-tbc-navy via-[#0f1e35] to-tbc-navy p-6 text-white overflow-hidden relative"
        data-testid="connect-card-no-account"
      >
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-tbc-gold/10 blur-2xl" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-tbc-gold/20 border border-tbc-gold/40 flex items-center justify-center flex-shrink-0">
            <BadgeDollarSign className="w-6 h-6 text-tbc-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg text-white">Set up payouts to accept orders</h3>
            <p className="text-sm text-white/70 font-body mt-1">
              Buyers can&apos;t check out until you finish Stripe&apos;s ~5-minute payout setup.
              You&apos;ll get paid daily, direct to your bank.
            </p>
          </div>
          <Button
            onClick={startOnboarding}
            disabled={busy}
            className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold rounded-full whitespace-nowrap"
            data-testid="connect-start-onboarding"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Set up payouts <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  // -------- Under review (details submitted, waiting on Stripe) --------
  if (state === "under_review") {
    return (
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-6" data-testid="connect-card-under-review">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg text-blue-900">Stripe is reviewing your information</h3>
            <p className="text-sm text-blue-800 font-body mt-1">
              Your details are in — Stripe usually finishes review within 24 hours. You&apos;ll
              be able to accept orders as soon as it&apos;s approved. We&apos;ll notify you the moment it&apos;s live.
            </p>
          </div>
        </div>
        <div className="mt-4">
          <Button variant="outline" onClick={fetchStatus} className="rounded-full" data-testid="connect-refresh-status">
            Refresh status
          </Button>
        </div>
      </div>
    );
  }

  // -------- Restricted (Stripe locked the account) --------
  if (state === "restricted") {
    return (
      <div className="rounded-2xl border border-red-300 bg-red-50 p-6" data-testid="connect-card-restricted">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg text-red-900">Payouts restricted</h3>
            <p className="text-sm text-red-800 font-body mt-1">
              Stripe has paused this account and needs more information before it can be reactivated.
              Open your Stripe dashboard to resolve the issue.
            </p>
            {status.requirements?.disabled_reason && (
              <p className="text-xs text-red-900/70 mt-2 italic">
                Reason: {status.requirements.disabled_reason.replace(/_/g, " ")}
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={openStripeDashboard}
            disabled={busy}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full"
            data-testid="connect-open-stripe-restricted"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open Stripe to resolve
          </Button>
          <Button variant="outline" onClick={fetchStatus} className="rounded-full">
            Refresh status
          </Button>
        </div>
      </div>
    );
  }

  // -------- Incomplete / Verification required --------
  if (state === "incomplete" || state === "verification_required") {
    const currentlyDue = status.requirements?.currently_due || [];
    return (
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6" data-testid="connect-card-incomplete">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-lg text-amber-900">
              {state === "verification_required" ? "Verification required" : "Finish setting up payouts"}
            </h3>
            <p className="text-sm text-amber-800 font-body mt-1">
              Stripe still needs a few details before buyers can check out from your listings.
            </p>
            {currentlyDue.length > 0 && (
              <ul className="mt-3 text-xs text-amber-900/80 font-body space-y-1">
                {currentlyDue.slice(0, 5).map((r) => (
                  <li key={r}>• {r.replace(/_/g, " ")}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button
            onClick={startOnboarding}
            disabled={busy}
            className="bg-amber-600 hover:bg-amber-700 text-white rounded-full"
            data-testid="connect-continue-onboarding"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Continue Stripe setup <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button variant="outline" onClick={fetchStatus} className="rounded-full">
            Refresh status
          </Button>
        </div>
      </div>
    );
  }

  // -------- Active --------
  return (
    <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-6" data-testid="connect-card-ready">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg text-emerald-900">
            Payments &amp; payouts active <span aria-hidden>✓</span>
          </h3>
          <p className="text-sm text-emerald-800 font-body mt-1">
            You&apos;re ready to accept paid orders.{" "}
            {status.payouts_enabled
              ? "Payouts land daily to your bank."
              : "Stripe is finalizing your payout schedule — you can already accept orders."}
          </p>
        </div>
      </div>
      <div className="mt-4">
        <Button
          onClick={openStripeDashboard}
          disabled={busy}
          variant="outline"
          size="sm"
          className="rounded-full border-emerald-400 text-emerald-800 hover:bg-emerald-100"
          data-testid="connect-open-dashboard"
        >
          {busy ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <ExternalLink className="w-3 h-3 mr-2" />}
          Manage payouts
        </Button>
      </div>
    </div>
  );
}
